import { FormEvent, ReactNode } from 'react';
import styled from 'styled-components';
import { rgba } from 'polished';

import { OrderType } from '#/api/orders';

import { cFFFFFF, mainGridGapColor } from '#/colors';

import {
  Tabs,
  TabList as ReachTabList,
  Tab as ReachTab,
  TabPanels,
  TabPanel,
} from '#/components/common/tabs';
import { gradientUnderline } from '#/components/shared/styles';

import {
  useOrderFormActions,
  useOrderFormView,
} from '#/features/perpetuals/order-builder/form-context';
import { OrderSubmitProvider } from '#/features/perpetuals/order-builder/submit-context';

import InputAmount from './InputAmount';
import InputLimitPrice from './InputLimitPrice';
import SubmitOrder from './SubmitOrder';
import OrderSummary from './OrderSummary';
import InstructionSelect from './InstructionSelect';

interface Props {
  readonly className?: string;
}

interface Tab {
  readonly id: OrderType;
  readonly label: string;
  readonly children: ReactNode;
}

const TABS: Tab[] = [
  {
    id: 'MARKET',
    label: 'Market',
    children: <InputAmount />,
  },
  {
    id: 'LIMIT',
    label: 'Limit',
    children: (
      <>
        <InputLimitPrice />
        <InputAmount />
        <InstructionSelect />
      </>
    ),
  },
];

export default function OrderBuilder(props: Props) {
  return (
    <OrderSubmitProvider>
      <OrderBuilderContent {...props} />
    </OrderSubmitProvider>
  );
}

function OrderBuilderContent({ className }: Props) {
  const formView = useOrderFormView();
  const formActions = useOrderFormActions();
  const activeTab = TABS.findIndex((tab) => tab.id === formView.type);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  };

  const handleTabChange = (idx: number) =>
    formActions.updateType(TABS[idx]!.id);

  return (
    <form className={className} onSubmit={handleSubmit}>
      <Tabs index={activeTab} onChange={handleTabChange}>
        <TabList>
          {TABS.map(({ id, label }) => (
            <Tab key={id}>{label}</Tab>
          ))}
        </TabList>
        <TabPanels>
          {TABS.map(({ id, children }) => (
            <TabPanel key={id}>
              <TabPanelContent>
                {children}
                <OrderSummary />
                <SubmitOrder />
              </TabPanelContent>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </form>
  );
}

const TabList = styled(ReachTabList)`
  height: 39px;
  border-bottom: 1px solid ${mainGridGapColor};
  padding: 0 16px 0;
`;

const Tab = styled(ReachTab)`
  color: ${rgba(cFFFFFF, 0.5)};
  font-size: 13px;
  font-weight: 700;
  border-bottom: none;
  margin-bottom: -1px;
  flex: 1 1 0px;
  z-index: 1;

  &[data-selected] {
    color: ${cFFFFFF};
    ${gradientUnderline}
  }
`;

const TabPanelContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 16px;
`;
